import {Button, Col, Form, Input, Modal, Row, Select, Tag, message} from 'antd';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';

import {RQUpdateUser} from '../../../../../ReactQuery/apis';
import {FIELD_DEFS, ROLE_FIELDS} from '../RegisterEmployee/RegisterEmployee';

const GENDER_OPTIONS = [
  {value: 'Male', label: 'Male'},
  {value: 'Female', label: 'Female'},
  {value: 'Other', label: 'Other'},
];

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

// Only send fields relevant to this employee's role + the common fields.
// Keeps the payload clean and avoids overwriting unrelated columns with undefined.
const buildUpdatePayload = (values, role) => {
  const extraFields = ROLE_FIELDS[role] || [];
  const allowedKeys = ['fullName', 'mobileNumber', 'address', 'gender', 'status', ...extraFields];

  return allowedKeys.reduce((acc, key) => {
    if (values[key] !== undefined) {
      acc[key] = values[key];
    }
    return acc;
  }, {});
};

export const EditEmployee = ({open, onClose, onSuccess, user, token}) => {
  const [form] = Form.useForm();
  const role = user?.role;
  const extraFields = ROLE_FIELDS[role] || [];

  const updateUser = RQUpdateUser({
    onSuccess: () => {
      message.success('Employee updated successfully');
      onSuccess?.();
      onClose();
    },
    onError: err => {
      message.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to update employee',
      );
    },
  });

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        fullName: user.fullName,
        mobileNumber: user.mobileNumber,
        address: user.address,
        gender: user.gender,
        status: user.status,
        specialization: user.specialization,
        licenseNumber: user.licenseNumber,
        medicalname: user.medicalname,
        availability: user.availability,
        workExperience: user.workExperience,
        qualification: user.qualification,
      });
    }
  }, [open, user, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = values => {
    if (!user?.id) {
      message.error('Missing user id, cannot update');
      return;
    }

    updateUser.mutate({
      id: user.id,
      formData: buildUpdatePayload(values, role),
      token,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      width={680}
      title={(
        <span className={'text-primary-700 font-bold text-base flex items-center gap-2'}>
          Edit Employee
          {role && <Tag color={'blue'}>{role}</Tag>}
        </span>
      )}
    >
      <Form form={form} layout={'vertical'} onFinish={handleFinish} className={'mt-4'}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={'Full Name'}
              name={'fullName'}
              rules={[{required: true, message: 'Please enter full name'}]}
            >
              <Input placeholder={'e.g. John Doe'} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Mobile Number'}
              name={'mobileNumber'}
              rules={[
                {required: true, message: 'Please enter mobile number'},
                {pattern: /^[0-9]{10}$/, message: 'Enter a valid 10-digit mobile number'},
              ]}
            >
              <Input placeholder={'9876543210'} maxLength={10} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Gender'}
              name={'gender'}
              rules={[{required: true, message: 'Please select gender'}]}
            >
              <Select placeholder={'Select Gender'} options={GENDER_OPTIONS} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={'Status'} name={'status'}>
              <Select options={STATUS_OPTIONS.map(item => ({value: item, label: item}))} />
            </Form.Item>
          </Col>

          {/* Role-specific fields — same source of truth as EmployeeRegistration */}
          {extraFields.map(fieldKey => {
            const def = FIELD_DEFS[fieldKey];
            return (
              <Col span={12} key={fieldKey}>
                <Form.Item
                  label={def.label}
                  name={fieldKey}
                  rules={def.required ? [{required: true, message: `Please enter ${def.label.toLowerCase()}`}] : []}
                >
                  <Input placeholder={def.placeholder} />
                </Form.Item>
              </Col>
            );
          })}

          <Col span={24}>
            <Form.Item label={'Address'} name={'address'} rules={[{required: true, message: 'Please enter address'}]}>
              <Input.TextArea rows={3} placeholder={'Enter Address'} />
            </Form.Item>
          </Col>
        </Row>

        <div className={'flex justify-end gap-2 mt-3'}>
          <Button onClick={handleCancel} className={'btn-secondary'}>
            Cancel
          </Button>
          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={updateUser.isPending}
            className={'btn-primary'}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

EditEmployee.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  user: PropTypes.object,
  token: PropTypes.string,
};

export default EditEmployee;
